import { useQuery } from "@tanstack/react-query";
import { authService } from "@/api";
import apiClient from "@/api/apiClient";
import { getUserRole, USER_TYPES } from "@/utils/roles";

// Fetch user permissions from backend
const fetchUserPermissions = async () => {
  const response = await apiClient.get("/api/permissions");
  return response;
};

export const useAuth = () => {
  const {
    data: currentUser,
    isLoading: userLoading,
    error: userError,
  } = useQuery({
    queryKey: ["currentUser"],
    queryFn: authService.getCurrentUser,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const {
    data: permissionsData,
    isLoading: permissionsLoading,
    error: permissionsError,
  } = useQuery({
    queryKey: ["userPermissions"],
    queryFn: fetchUserPermissions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
    enabled: !!currentUser, // Only fetch permissions if user is loaded
  });

  const isLoading = userLoading || permissionsLoading;
  const error = userError || permissionsError;
  const isAuthenticated = !!currentUser && !isLoading;
  const role = getUserRole(currentUser);
  const userPermissions = permissionsData?.permissions;
  const navigationPermissions = permissionsData?.navigationPermissions;

  // Helper functions using fetched permissions
  const hasPermission = (permission) => {
    if (!userPermissions || !Array.isArray(userPermissions.permissions))
      return false;
    return userPermissions.permissions.includes(permission);
  };

  const hasAnyPermission = (permissionList) => {
    if (
      !userPermissions ||
      !Array.isArray(userPermissions.permissions) ||
      !permissionList?.length
    )
      return false;
    return permissionList.some((permission) =>
      userPermissions.permissions.includes(permission)
    );
  };

  const hasAllPermissions = (permissionList) => {
    if (
      !userPermissions ||
      !Array.isArray(userPermissions.permissions) ||
      !permissionList?.length
    )
      return false;
    return permissionList.every((permission) =>
      userPermissions.permissions.includes(permission)
    );
  };

  const canAccessPatient = (patientId) => {
    if (!userPermissions || !Array.isArray(userPermissions.patientAccess))
      return false;
    return userPermissions.patientAccess.includes(patientId);
  };

  const canAccessDoctor = (doctorId) => {
    if (!userPermissions || !Array.isArray(userPermissions.doctorAccess))
      return false;
    return userPermissions.doctorAccess.includes(doctorId);
  };

  const canAccessPractice = (practiceId) => {
    if (!userPermissions || !Array.isArray(userPermissions.practiceAccess))
      return false;
    return userPermissions.practiceAccess.includes(practiceId);
  };

  return {
    user: currentUser,
    isLoading,
    error,
    isAuthenticated,
    role,
    permissions: userPermissions?.permissions || [],
    navigationPermissions,

    // Role checks
    isPatient: role === USER_TYPES.PATIENT,
    isDoctor: role === USER_TYPES.DOCTOR || role === USER_TYPES.PRACTICE_OWNER,
    isPracticeOwner: role === USER_TYPES.PRACTICE_OWNER,

    // Permission checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    // Access checks
    canAccessPatient,
    canAccessDoctor,
    canAccessPractice,

    // Accessible entities
    accessiblePatients: userPermissions?.patientAccess || [],
    accessibleDoctors: userPermissions?.doctorAccess || [],
    accessiblePractices: userPermissions?.practiceAccess || [],
  };
};

export const usePermissions = () => {
  const { user, hasPermission, hasAnyPermission, hasAllPermissions } =
    useAuth();

  return {
    user,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
};

export const useRole = () => {
  const { user, role, isPatient, isDoctor, isPracticeOwner } = useAuth();

  return {
    user,
    role,
    isPatient,
    isDoctor,
    isPracticeOwner,
  };
};
